/**
 * Figmawya Quiz API — course section question-gating (add-on).
 *
 * Talks to the new endpoints in figmawya-quiz-course-sections.php:
 *   GET  /courses/{id}/sections                     — gated sections + progress + running score
 *   GET  /courses/{id}/sections/{key}/questions     — only the section's selected questions
 *   POST /courses/{id}/sections/{key}/submit        — grade + accumulate course score
 *   POST /courses/{id}/certificate/claim            — issue-if-eligible (manual fallback to auto-issue)
 *
 * These are additive; whole-quiz sections still use the existing learner-state flow.
 */

const BASE_URL =
    (typeof process !== "undefined" && process.env.NEXT_PUBLIC_BASE_URL
        ? process.env.NEXT_PUBLIC_BASE_URL
        : "") + "api/figmawya/v1";

const getAuthHeaders = () => {
    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
};

/**
 * Gated sections for a course + this user's progress and running score.
 * Returns null on error or when the add-on isn't available (older plugin).
 *
 * @param {string|number} courseId
 * @returns {Promise<{
 *   courseId: number,
 *   sections: { key: string, title: string, quizId: number, questionCount: number,
 *               threshold: number, passed: boolean, percentage: number, score: number,
 *               maxScore: number, attempts: number, locked: boolean,
 *               hasReading: boolean, readingConfirmed: boolean }[],
 *   courseScore: number, courseMaxScore: number, configuredMaxScore: number,
 *   coursePercentage: number, courseCompleted: boolean,
 *   certificate: { certPostId: number, hasCert: boolean, certVerifyUrl: string|null } | null
 * } | null>}
 */
export async function fetchCourseSections(courseId) {
    if (typeof window === "undefined" || courseId == null || courseId === "") return null;
    try {
        const res = await fetch(
            `${BASE_URL}/courses/${encodeURIComponent(String(courseId))}/sections`,
            { method: "GET", headers: getAuthHeaders(), cache: "no-store" }
        );
        if (!res.ok) return null;
        return await res.json();
    } catch (e) {
        console.warn("[course sections]", e);
        return null;
    }
}

/**
 * The specific questions for a gated section (safe DTOs, no answers).
 * @param {string|number} courseId
 * @param {string} sectionKey
 * @returns {Promise<{ ok: boolean, status: number, questions?: unknown[], threshold?: number, error?: string }>}
 */
export async function fetchSectionQuestions(courseId, sectionKey) {
    try {
        const res = await fetch(
            `${BASE_URL}/courses/${encodeURIComponent(String(courseId))}/sections/${encodeURIComponent(
                String(sectionKey)
            )}/questions`,
            { method: "GET", headers: getAuthHeaders(), cache: "no-store" }
        );
        const data = await res.json().catch(() => ({}));
        return { ok: res.ok, status: res.status, ...data };
    } catch (e) {
        return { ok: false, status: 0, error: e?.message || "network" };
    }
}

/**
 * "طلب شهادتي" — manual fallback for the same auto-issue path course completion
 * already triggers server-side. Safe to call anytime: a no-op if already issued,
 * a clear 403 (with `error: "not_completed"`) if the course isn't finished yet.
 * @param {string|number} courseId
 * @returns {Promise<{ ok: boolean, status: number, success?: boolean,
 *   certificate?: { certPostId: number, hasCert: boolean, certVerifyUrl: string|null },
 *   error?: string, message?: string }>}
 */
export async function claimCertificate(courseId) {
    try {
        const res = await fetch(
            `${BASE_URL}/courses/${encodeURIComponent(String(courseId))}/certificate/claim`,
            { method: "POST", headers: getAuthHeaders() }
        );
        const data = await res.json().catch(() => ({}));
        return { ok: res.ok, status: res.status, ...data };
    } catch (e) {
        return { ok: false, status: 0, error: e?.message || "network" };
    }
}

/**
 * Confirms the honor-code checkbox for a section's reading lesson. Must succeed before
 * that section's /questions or /submit will accept a section that has reading content.
 * @param {string|number} courseId
 * @param {string} sectionKey
 * @returns {Promise<{ ok: boolean, status: number, readingConfirmed?: boolean, course?: object, error?: string, message?: string }>}
 */
export async function confirmSectionReading(courseId, sectionKey) {
    try {
        const res = await fetch(
            `${BASE_URL}/courses/${encodeURIComponent(String(courseId))}/sections/${encodeURIComponent(
                String(sectionKey)
            )}/confirm-reading`,
            { method: "POST", headers: getAuthHeaders() }
        );
        const data = await res.json().catch(() => ({}));
        return { ok: res.ok, status: res.status, ...data };
    } catch (e) {
        return { ok: false, status: 0, error: e?.message || "network" };
    }
}

/**
 * Submit answers for a gated section. Grades + accumulates the course score.
 * @param {string|number} courseId
 * @param {string} sectionKey
 * @param {{ uid: string, type: string, value?: unknown, values?: unknown[], text?: string }[]} answers
 * @returns {Promise<{ ok: boolean, status: number, passed?: boolean, percentage?: number,
 *   score?: number, maxScore?: number, threshold?: number, nextSectionUnlocked?: boolean,
 *   course?: object, error?: string }>}
 */
export async function submitSectionQuiz(courseId, sectionKey, answers) {
    try {
        const res = await fetch(
            `${BASE_URL}/courses/${encodeURIComponent(String(courseId))}/sections/${encodeURIComponent(
                String(sectionKey)
            )}/submit`,
            {
                method: "POST",
                headers: getAuthHeaders(),
                body: JSON.stringify({ answers }),
            }
        );
        const data = await res.json().catch(() => ({}));
        return { ok: res.ok, status: res.status, ...data };
    } catch (e) {
        return { ok: false, status: 0, error: e?.message || "network" };
    }
}
